import { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { UseQueryResult } from "@tanstack/react-query";
import type { CheckoutFormData } from "../checkout.types";
import { fetchCart } from "@/networking/carts.api";
import { getPaymentBehavior } from "@/lib/utils";
import { PaymentBehavior } from "@/networking/payments.api";

export function useSetDefaultCheckoutValues(
  checkoutForm: UseFormReturn<CheckoutFormData>,
  query: UseQueryResult<Awaited<ReturnType<typeof fetchCart>>>,
) {
  const hasSetDefaultValues = useRef(false);

  useEffect(() => {
    if (!hasSetDefaultValues.current && query?.data?.cart) {
      const cart = query.data.cart;

      // Country info - set on the appropriate address form based on digital_only
      if (cart.digital_only) {
        checkoutForm.setValue(
          "customer.payment_method.billing_address.country",
          cart.country.name,
        );
        checkoutForm.setValue(
          "customer.payment_method.billing_address.country_code",
          cart.country.iso,
        );
      } else {
        checkoutForm.setValue(
          "customer.shipping_address.country",
          cart.country.name,
        );
        checkoutForm.setValue(
          "customer.shipping_address.country_code",
          cart.country.iso,
        );
      }

      // Email if exists
      if (cart.email) {
        checkoutForm.setValue("customer.contact_info.email", cart.email);
      }

      // Phone if exists
      const cartWithPhone = cart as typeof cart & { phone?: string };
      if (cartWithPhone.phone) {
        checkoutForm.setValue(
          "customer.contact_info.phone",
          cartWithPhone.phone,
        );
      }

      // Pre-fill address from cart data
      if (cart.digital_only) {
        if (cart.bill_to) {
          const address = cart.bill_to;
          checkoutForm.setValue(
            "customer.payment_method.billing_address.name",
            address.name || "",
          );
          checkoutForm.setValue(
            "customer.payment_method.billing_address.address1",
            address.address1 || "",
          );
          checkoutForm.setValue(
            "customer.payment_method.billing_address.address2",
            address.address2 || "",
          );
          checkoutForm.setValue(
            "customer.payment_method.billing_address.city",
            address.city || "",
          );
          checkoutForm.setValue(
            "customer.payment_method.billing_address.state",
            address.state || "",
          );
          checkoutForm.setValue(
            "customer.payment_method.billing_address.postal_code",
            address.postal_code || "",
          );
          checkoutForm.setValue(
            "customer.payment_method.billing_address.country_code",
            address.country_code || cart.country.iso,
          );
        }
      } else if (cart.ship_to) {
        const address = cart.ship_to;
        checkoutForm.setValue(
          "customer.shipping_address.name",
          address.name || "",
        );
        checkoutForm.setValue(
          "customer.shipping_address.address1",
          address.address1 || "",
        );
        checkoutForm.setValue(
          "customer.shipping_address.address2",
          address.address2 || "",
        );
        checkoutForm.setValue(
          "customer.shipping_address.city",
          address.city || "",
        );
        checkoutForm.setValue(
          "customer.shipping_address.state",
          address.state || "",
        );
        checkoutForm.setValue(
          "customer.shipping_address.postal_code",
          address.postal_code || "",
        );
        checkoutForm.setValue(
          "customer.shipping_address.country_code",
          address.country_code || cart.country.iso,
        );
      }

      // Shipping method if exists
      if (cart.shipping_method?.id) {
        checkoutForm.setValue(
          "customer.shipping_method.id",
          cart.shipping_method.id,
        );
      }

      // Payment method priority:
      // 1. If cart has payment_method.type === "CreditCard", find matching credit card in available methods
      // 2. Default to first credit card method (using PaymentBehavior enum)
      // 3. If no credit card, use first available option
      const availableMethods = cart?.available_payment_methods || [];

      if (cart?.payment_method?.type === "CreditCard") {
        // Cart has CreditCard selected, find the actual credit_card option
        const creditCardMethod = availableMethods.find(
          (method) =>
            getPaymentBehavior(method.type) === PaymentBehavior.CREDIT_CARD,
        );

        if (creditCardMethod) {
          checkoutForm.setValue(
            "customer.payment_method.type",
            creditCardMethod.type,
          );
        } else {
          console.warn("⚠️ No credit card method found in available methods");
        }
      } else {
        // Find first credit card method using PaymentBehavior enum
        const creditCardMethod = availableMethods.find(
          (method) =>
            getPaymentBehavior(method.type) === PaymentBehavior.CREDIT_CARD,
        );

        const defaultMethod =
          creditCardMethod?.type || availableMethods[0]?.type;

        if (defaultMethod) {
          checkoutForm.setValue("customer.payment_method.type", defaultMethod);
        }
      }

      // Agreements
      if (cart.agreements?.length > 0) {
        checkoutForm.setValue("customer.agreements_accepted", false);
      }

      // billing address defaults
      if (cart.digital_only) {
        checkoutForm.setValue(
          "customer.payment_method.use_shipping_address_for_billing",
          false,
        );
      } else {
        const billToId = cart.bill_to?.id;
        const shipToId = cart.ship_to?.id;
        const hasDifferentBillingAddress =
          billToId && shipToId && billToId !== shipToId;

        checkoutForm.setValue(
          "customer.payment_method.use_shipping_address_for_billing",
          !hasDifferentBillingAddress,
        );

        if (hasDifferentBillingAddress && cart.bill_to) {
          checkoutForm.setValue("customer.payment_method.billing_address", {
            name: cart.bill_to.name || "",
            address1: cart.bill_to.address1 || "",
            address2: cart.bill_to.address2 || "",
            city: cart.bill_to.city || "",
            state: cart.bill_to.state || "",
            postal_code: cart.bill_to.postal_code || "",
            country: cart.bill_to.country_code || "",
            country_code: cart.bill_to.country_code || "",
          });
        }
      }

      hasSetDefaultValues.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query?.data?.cart?.id,
    query?.data?.cart?.email,
    query?.data?.cart?.ship_to?.id,
    query?.data?.cart?.bill_to?.id,
    checkoutForm.setValue,
  ]);
}
